import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Layers } from '@modules/core';
import { useZed } from '@modules/core/src/react/useZed';
import { StandardModule } from '@modules/standard/src/StandardModule';
import zams from '@zams/logger/zed';
import { useCallback, useEffect, useState } from 'react';

import { UnitResponse } from '../ecs/component/response/Unit';
import { getTourDanjiZamsParams, TourDanjiModule } from '../TourDanjiModule';

import pauseButton from './assets/daylight_pause.svg';
import playButton from './assets/daylight_play.svg';
import { useScreen } from './hooks/useScreen';
import styles from './styles/InteriorViewDaylightPanel.module.css';

type SeasonType = '봄' | '여름' | '가을' | '겨울';
type PlayButtonStatus = '재생' | '일시정지';

export function InteriorViewDaylightPanel() {
  const seasons = ['봄', '여름', '가을', '겨울'];
  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();
  const [isPlaying, setPlaying] = useState<boolean>(false);
  const [seasonIndex, setSeasonIndex] = useState<number>(0);
  const [time, setTime] = useState<Date>(new Date());
  const [unitResponse, setUnitResponse] = useState<UnitResponse | undefined>(undefined);
  
  const screenInfo = useScreen();
  const { isDesktop } = screenInfo;

  useEffect(() => {
    if (!zed) return;

    setTime(zed.api.getCurrentDate());
    const onUnitActivated = zed.api.observer.onUnitActivated.add((data) => {
      setUnitResponse(data);
    });

    return () => {
      onUnitActivated.remove();
    };
  }, [zed]);

  useEffect(() => {
    if (zed?.api.observer.onTimeIsRunning.hasObservers()) {
      zed?.api.observer.onTimeIsRunning.notifyObservers(isPlaying);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!zed) return;
    const timerId = setInterval(() => {
      setTime(zed.api.getCurrentDate());
    }, 100);
    return () => {
      clearInterval(timerId);
    };
  }, [zed]);

  const handleSeasonChange = (index: number) => {
    zams.z138({
      ...getZamsParams(),
      status: seasons[index] as SeasonType,
    }).send();
    setSeasonIndex(index);
    if (zed?.api.observer.onSeasonChanged.hasObservers()) {
      zed?.api.observer.onSeasonChanged.notifyObservers(index);
    }
  }

  const handleTimeChange = () => {
    const season = seasons[seasonIndex] as SeasonType;
    const buttonName = isPlaying ? '일시정지' : '재생';
    setPlaying(!isPlaying);
    zams.z139({
      ...getZamsParams(),
      status: season,
      button_name: buttonName as PlayButtonStatus,
    }).send();
  }

  const getZamsParams = useCallback(() => {
    type ScreenStatus = '실내' | '실외';
    return {
      ...getTourDanjiZamsParams(),
      screen_status: '실내' as ScreenStatus,
      zed_dong: unitResponse?.buildingName ?? '',
      zed_ho: unitResponse?.unitName ?? '',
    }
  }, [unitResponse]);

  if (!unitResponse) return null;

  return (
    <div style={{ zIndex: Layers.UI_CONTENTS }} className={styles.root}>
      <div className={`${styles.controlPanel} ${isDesktop ? styles.controlPanelDesktop : ''}`}>
        <div className={`${styles.panelContainer} ${isDesktop ? styles.panelContainerDesktop : ''}`}>
          <div className={styles.panelTimerContainer}>
            <div className={styles.panelTimer}>
            <div className={`${styles.panelTimerPrefix} ${isDesktop ? styles.panelTimerPrefixDesktop : ''}`}>{`${time.getHours() >= 12 ? 'PM' : 'AM'}`}</div>
            <div className={`${styles.panelTimerTimeText} ${isDesktop ? styles.panelTimerTimeTextDesktop : ''}`}>
              {`${(time.getHours() % 12 || 12).toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`.split('').map((char, index) => (
                <span key={`timer-${index}`} className={char === ':' ? `${styles.timeColon} ${isDesktop ? styles.timeColonDesktop : ''}` : `${styles.timeChar} ${isDesktop ? styles.timeCharDesktop : ''}`}>{char}</span>
              ))}
            </div>
            <div className={`${styles.panelTimerButtonContainer} ${isDesktop ? styles.panelTimerButtonContainerDesktop : ''}`}>
              <div className={`${styles.panelTimerButton} ${isPlaying ? styles.panelTimerButtonActive : ''} ${isDesktop ? styles.panelTimerButtonActiveDesktop : ''} ${isDesktop ? styles.panelTimerButtonDesktop : ''}`}
                onClick={handleTimeChange}>
                {isPlaying ?
                <img src={pauseButton} className={`${styles.panelTimerButtonIcon} ${isDesktop ? styles.panelTimerButtonIconDesktop : ''}`} /> :
                  <img src={playButton} className={`${styles.panelTimerButtonIcon} ${isDesktop ? styles.panelTimerButtonIconDesktop : ''}`} />}
                </div>      
              </div>
            </div>
          </div>
          <div className={`${styles.panelSeasonButtonContainer} ${isDesktop ? styles.panelSeasonButtonContainerDesktop : ''}`}>
            {seasons.map((season, index) => (
              <div 
                key={`season-${index}`}
                className={`${styles.panelSeasonButton} ${isDesktop ? styles.panelSeasonButtonDesktop : ''} ${seasonIndex === index ? styles.panelSeasonButtonActive : ''}`}
                onClick={() => handleSeasonChange(index)}
                >
                  <div className={styles.panelSeasonButtonContentsContainer}>
                  <div className={`${styles.panelSeasonButtonText} ${isDesktop ? styles.panelSeasonButtonTextDesktop : ''}`}>{season}</div>
                  </div>
              </div>
            ))}
          </div>
          </div>
        </div>
    </div>
  );
}
